import React, { MutableRefObject } from 'react';
import {
  Button,
  CreditCard,
  Dropdown,
  OptionType,
} from '@chargify/custom-ui-components';
import { useTranslation } from 'react-i18next';

import { usePaymentProfileAvailableActions } from '../../hooks';

import { getPaymentMethodOptions } from './utils';
import { PAYMENT_PROFILE_STATUS } from './constants';

import type { PaymentProfile } from '../../models/paymentProfile';
import { PaymentProfileStatus } from '../../models';

export type PaymentProfileContainerProps = {
  /**
   * Payment profile model
   */
  paymentProfile: PaymentProfile;
  /**
   * Profile component size
   */
  componentSize: 'small' | 'regular';
  /**
   * Toggle dropdown event handler
   */
  onToggleDropdown: (profileId: number | null) => void;
  /**
   * Select option event handler
   */
  onSelectOption: (option: OptionType, profileId: number) => void;
  /**
   * Dropdown open indicator
   */
  isDropdownOpen?: boolean;
  /**
   * Container used to mount dropdown component
   */
  portalContainerRef: MutableRefObject<HTMLElement>;
};

const PaymentProfileContainer = ({
  paymentProfile,
  portalContainerRef,
  isDropdownOpen = false,
  onToggleDropdown,
  onSelectOption,
  componentSize,
}: PaymentProfileContainerProps) => {
  const { t } = useTranslation(['subscriptions-manager']);

  const availableActions = usePaymentProfileAvailableActions(
    paymentProfile.status
  );
  const { id, type, creditCard, address } = paymentProfile;

  const status = creditCard?.expired
    ? 'error'
    : PAYMENT_PROFILE_STATUS[paymentProfile.status];

  const slot = () =>
    paymentProfile.status !== PaymentProfileStatus.UNAVAILABLE ? (
      <Dropdown
        maxWidth={215}
        isOpen={isDropdownOpen}
        onOptionSelect={(option) => onSelectOption(option, id)}
        options={getPaymentMethodOptions(type, t, availableActions)}
        onClose={() => onToggleDropdown(null)}
        portalContainer={portalContainerRef}
        alignment="right"
      >
        <Button
          onClick={() => onToggleDropdown(isDropdownOpen ? null : id)}
          label=""
          iconLeft="ellipsis"
          fill="outline"
          color="secondary"
        />
      </Dropdown>
    ) : null;

  const renderPaymentProfile = () => {
    switch (paymentProfile.type) {
      case 'credit_card':
        return (
          <CreditCard
            status={status}
            renderSlot={slot}
            size={componentSize}
            key={id}
            card={{
              ...creditCard,
              type: creditCard?.cardType,
            }}
            addressDetails={address}
          />
        );
      default:
        return null;
    }
  };

  return renderPaymentProfile();
};

export default PaymentProfileContainer;
